import EmblaCarousel from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import AutoScroll from "embla-carousel-auto-scroll";
import { parseJsonAttr } from "./utils";

/**
 * 汎用スライダー（Embla Carousel）
 *
 * マークアップ規約
 *   <div class="slider" data-slider
 *        data-slider-options='{"loop":true,"align":"start"}'   ← 任意（Embla のオプションをJSONで）
 *        data-slider-autoplay="4000"                            ← 任意（自動再生の間隔ms）
 *        data-slider-autoscroll="1.2">                          ← 任意（等速スクロールの速度）
 *     <div class="slider__viewport">
 *       <div class="slider__container">
 *         <div class="slider__slide">…</div>   ← 何枚でもよい
 *       </div>
 *     </div>
 *     <button type="button" data-slider-prev>…</button>  ← 任意
 *     <button type="button" data-slider-next>…</button>  ← 任意
 *     <div data-slider-dots></div>                        ← 任意（枚数に応じて自動生成）
 *   </div>
 *
 * スライド枚数に依存する処理は書かない。増減してもそのまま動く。
 *
 * loop: true を使うときの注意
 *   Embla のループは「スライド全体の幅」を1周ぶんとして使い回す仕組みのため、
 *   全体幅がビューポート幅を十分に上回っていないと、折り返し地点で端に隙間ができる。
 *   目安は「スライド全体の幅 >= ビューポート幅 × 2」。
 *   幅いっぱいに広げて左右を見切れさせる場合は --slider-slide-size を小さくするか、
 *   スライド枚数を増やして調整すること。
 */

const SLIDER_SELECTOR = "[data-slider]";

/** Embla の既定オプション。data-slider-options で個別に上書きできる */
const DEFAULT_OPTIONS = {
  loop: false,
  align: "start",
  dragFree: false,
  containScroll: "trimSnaps",
};

/** ルート要素 → Embla インスタンス */
const instances = new WeakMap();

/**
 * ドットを枚数ぶん生成し、選択状態を同期する
 *
 * @param {Object} embla - Embla インスタンス
 * @param {HTMLElement} dotsRoot
 * @return {void}
 */
const setupDots = (embla, dotsRoot) => {
  const createDots = () => {
    dotsRoot.innerHTML = "";

    embla.scrollSnapList().forEach((_, index) => {
      const dot = document.createElement("button");
      dot.className = "slider__dot";
      dot.type = "button";
      dot.setAttribute("role", "tab");
      dot.setAttribute("aria-selected", "false");
      dot.setAttribute("aria-label", `スライド${index + 1}`);
      dot.addEventListener("click", () => embla.scrollTo(index));
      dotsRoot.appendChild(dot);
    });
  };

  const updateDots = () => {
    const selectedIndex = embla.selectedScrollSnap();
    dotsRoot.querySelectorAll(".slider__dot").forEach((dot, index) => {
      const isActive = index === selectedIndex;
      dot.classList.toggle("slider__dot--active", isActive);
      dot.setAttribute("aria-selected", isActive ? "true" : "false");
    });
  };

  embla.on("reInit", createDots);
  embla.on("reInit", updateDots);
  embla.on("select", updateDots);

  createDots();
  updateDots();
};

/**
 * 前後ボタンを配線し、端で disabled にする
 *
 * @param {Object} embla
 * @param {HTMLElement|null} prevButton
 * @param {HTMLElement|null} nextButton
 * @return {void}
 */
const setupArrows = (embla, prevButton, nextButton) => {
  if (!prevButton && !nextButton) return;

  prevButton?.addEventListener("click", () => embla.scrollPrev());
  nextButton?.addEventListener("click", () => embla.scrollNext());

  const updateArrows = () => {
    if (prevButton) prevButton.disabled = !embla.canScrollPrev();
    if (nextButton) nextButton.disabled = !embla.canScrollNext();
  };

  embla.on("select", updateArrows);
  embla.on("reInit", updateArrows);
  updateArrows();
};

/**
 * data 属性から Embla プラグインを組み立てる
 *
 * @param {HTMLElement} node
 * @return {Array}
 */
const buildPlugins = (node) => {
  const plugins = [];

  const autoplayDelay = Number(node.dataset.sliderAutoplay);
  if (Number.isFinite(autoplayDelay) && autoplayDelay > 0) {
    plugins.push(Autoplay({ delay: autoplayDelay, stopOnInteraction: false }));
  }

  const autoScrollSpeed = Number(node.dataset.sliderAutoscroll);
  if (Number.isFinite(autoScrollSpeed) && autoScrollSpeed > 0) {
    plugins.push(
      AutoScroll({
        speed: autoScrollSpeed,
        startDelay: 0,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        stopOnFocusIn: false,
      }),
    );
  }

  return plugins;
};

/**
 * スライダーを初期化する（冪等）
 * root を渡せるので、モーダルなど後から挿入されたDOMにも適用できる
 *
 * @param {Document|HTMLElement} root
 * @param {Object} [callbacks]
 * @param {Function} [callbacks.onLayout] - 初期化・リサイズ後に呼ばれる（外側のレイアウト再計算用）
 * @return {Array} 生成した { node, embla } の配列
 */
export const initSliders = (root = document, { onLayout } = {}) => {
  const nodes = root.querySelectorAll?.(SLIDER_SELECTOR);
  if (!nodes?.length) return [];

  const created = [];

  nodes.forEach((node) => {
    if (node.dataset.sliderReady === "true") return;

    const viewport = node.querySelector("[data-slider-viewport]") || node.querySelector(".slider__viewport");
    if (!viewport) return;

    const options = { ...DEFAULT_OPTIONS, ...parseJsonAttr(node, "sliderOptions") };
    const embla = EmblaCarousel(viewport, options, buildPlugins(node));

    const dotsRoot = node.querySelector("[data-slider-dots]");
    if (dotsRoot) setupDots(embla, dotsRoot);

    setupArrows(embla, node.querySelector("[data-slider-prev]"), node.querySelector("[data-slider-next]"));

    if (typeof onLayout === "function") {
      embla.on("init", onLayout);
      embla.on("resize", onLayout);
    }

    instances.set(node, embla);
    node.dataset.sliderReady = "true";
    created.push({ node, embla });
  });

  return created;
};

/**
 * スライダーを破棄する（モーダルを閉じたときなど）
 *
 * @param {Document|HTMLElement} root
 * @return {void}
 */
export const destroySliders = (root = document) => {
  const nodes = root.querySelectorAll?.(SLIDER_SELECTOR);
  if (!nodes?.length) return;

  nodes.forEach((node) => {
    instances.get(node)?.destroy();
    instances.delete(node);
    delete node.dataset.sliderReady;
  });
};

/**
 * ルート要素から Embla インスタンスを取り出す
 *
 * @param {HTMLElement} node
 * @return {Object|undefined}
 */
export const getSlider = (node) => instances.get(node);
